"use client"

import Link from "next/link"
import { useState } from "react"
import { usePathname } from "next/navigation"
import { BrandLogo } from "@/components/brand-logo"
import { useIsMounted } from "@/hooks/use-is-mounted"
import { Button } from "@/components/ui/button"
import { BookOpen, GraduationCap, LayoutDashboard, Menu, MonitorPlay } from "lucide-react"
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet"
import { useAuth } from "@/hooks/use-auth"
import { cn, btn3d } from "@/lib/utils"

export function Navbar() {
    const { user, isLoading } = useAuth()
    const [isOpen, setIsOpen] = useState(false)
    const pathname = usePathname()

    // Whether someone is signed in is knowable only in the browser, but the
    // server still has to render *something* here. It rendered the skeleton
    // while the client's first pass rendered the logged-out buttons, so React
    // threw a hydration mismatch on every load of the landing page and threw
    // the whole header subtree away to re-render it.
    //
    // Holding the skeleton until after mount makes both passes agree; the real
    // state lands on the very next commit.
    const mounted = useIsMounted()
    const isAuthLoading = !mounted || isLoading

    const navLinks: { href: string; label: string; icon: typeof GraduationCap; external?: boolean }[] = [
        { href: "https://www.dsatschool.com/courses", label: "Courses", icon: BookOpen, external: true },
        { href: "/tools/university-finder", label: "Find Universities", icon: GraduationCap },
    ]
    const dashboardActive = pathname.startsWith("/dashboard")

    return (
        <>
            <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="container flex h-16 w-full items-center justify-between px-6 md:px-8 mx-auto">
                    <div className="flex items-center gap-8">
                        <Link href="/" className="flex items-center rounded-md transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                            <BrandLogo />
                        </Link>
                    </div>

                    <nav className="absolute left-1/2 hidden -translate-x-1/2 md:flex items-center justify-center gap-3">
                        {navLinks.map((link) => {
                            const isActive = !link.external && pathname.startsWith(link.href)
                            const Icon = link.icon

                            return (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    target={link.external ? "_blank" : undefined}
                                    rel={link.external ? "noreferrer" : undefined}
                                    className={cn(
                                        "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold",
                                        btn3d,
                                        isActive
                                            ? "border-primary/30 bg-primary/10 text-primary"
                                            : "border-transparent bg-muted/50 text-foreground hover:border-border hover:bg-muted"
                                    )}
                                >
                                    <Icon className="h-4 w-4" />
                                    <span>{link.label}</span>
                                </Link>
                            )
                        })}
                    </nav>

                    <div className="flex items-center gap-3">
                        <div className="hidden md:flex items-center gap-3">
                            {isAuthLoading ? (
                                // Stable placeholder — same width as the unauthenticated buttons
                                // prevents layout shift while the auth status check resolves
                                <div className="flex items-center gap-3" aria-hidden="true">
                                    <div className="h-9 w-16 rounded-md bg-muted/60 animate-pulse" />
                                    <div className="h-9 w-24 rounded-md bg-muted/60 animate-pulse" />
                                </div>
                            ) : user ? (
                                <div className="flex items-center gap-3">
                                    <Button asChild size="sm" variant="ghost" className={cn("h-10 rounded-full border border-border/60 bg-muted/50 px-4 text-sm font-semibold text-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground", btn3d)}>
                                        <Link href="/go-to-lms" target="_blank" rel="noreferrer">
                                            <MonitorPlay className="mr-2 h-4 w-4" />
                                            My Courses
                                        </Link>
                                    </Button>
                                    <Link href="/dashboard">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className={cn(
                                                "h-10 rounded-full border px-4 text-sm font-semibold",
                                                btn3d,
                                                dashboardActive
                                                    ? "border-primary/30 bg-primary/10 text-primary hover:border-primary hover:bg-primary hover:text-primary-foreground"
                                                    : "border-border/60 bg-muted/50 text-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground"
                                            )}
                                        >
                                            <LayoutDashboard className="mr-2 h-4 w-4" />
                                            Dashboard
                                        </Button>
                                    </Link>
                                </div>
                            ) : (
                                <>
                                    <Link href="/login">
                                        <Button variant="ghost" size="sm" className="text-sm font-medium">Log in</Button>
                                    </Link>
                                    <Link href="/register">
                                        <Button size="sm" className="font-medium shadow-sm text-sm">Get Started</Button>
                                    </Link>
                                </>
                            )}
                        </div>

                        <div className="md:hidden flex items-center gap-2">
                            <Sheet open={isOpen} onOpenChange={setIsOpen}>
                                <SheetTrigger asChild>
                                    <Button variant="ghost" size="icon" className="md:hidden" suppressHydrationWarning>
                                        <Menu className="h-5 w-5" />
                                        <span className="sr-only">Toggle menu</span>
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                                    <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                                    <div className="flex flex-col gap-6 mt-6">
                                        <Link href="/" className="flex items-center" onClick={() => setIsOpen(false)}>
                                            <BrandLogo />
                                        </Link>
                                        <nav className="flex flex-col gap-4">
                                            {navLinks.map((link) => (
                                                <Link
                                                    key={link.href}
                                                    href={link.href}
                                                    target={link.external ? "_blank" : undefined}
                                                    rel={link.external ? "noreferrer" : undefined}
                                                    className="block rounded-xl border border-border/60 bg-muted/40 px-4 py-3 text-lg font-medium transition-colors hover:border-primary/30 hover:text-primary"
                                                    onClick={() => setIsOpen(false)}
                                                >
                                                    <span className="flex items-center gap-2">
                                                        <link.icon className="h-5 w-5" />
                                                        <span>{link.label}</span>
                                                    </span>
                                                </Link>
                                            ))}
                                            <hr className="my-2 border-border" />
                                            {isAuthLoading ? (
                                                <div className="flex flex-col gap-3" aria-hidden="true">
                                                    <div className="h-12 w-full rounded-xl bg-muted/60 animate-pulse" />
                                                </div>
                                            ) : user ? (
                                                <div className="space-y-3">
                                                    <Button asChild variant="ghost" className="w-full justify-start rounded-xl border border-border/60 bg-muted/40 px-4 py-3 text-base font-medium text-foreground transition-all duration-200 hover:border-primary hover:bg-primary hover:text-primary-foreground">
                                                        <Link href="/go-to-lms" target="_blank" rel="noreferrer" onClick={() => setIsOpen(false)}>
                                                            <MonitorPlay className="mr-2 h-5 w-5" />
                                                            My Courses
                                                        </Link>
                                                    </Button>
                                                    <Link href="/dashboard" onClick={() => setIsOpen(false)}>
                                                        <Button
                                                            variant="ghost"
                                                            className={cn(
                                                                "w-full justify-start rounded-xl border px-4 py-3 text-base font-medium transition-all duration-200",
                                                                dashboardActive
                                                                    ? "border-primary/30 bg-primary/10 text-primary hover:border-primary hover:bg-primary hover:text-primary-foreground"
                                                                    : "border-border/60 bg-muted/40 text-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground"
                                                            )}
                                                        >
                                                            <LayoutDashboard className="mr-2 h-5 w-5" />
                                                            Dashboard
                                                        </Button>
                                                    </Link>
                                                </div>
                                            ) : (
                                                <>
                                                    <Link href="/login" onClick={() => setIsOpen(false)}>
                                                        <Button variant="outline" className="w-full justify-start">Log in</Button>
                                                    </Link>
                                                    <Link href="/register" onClick={() => setIsOpen(false)}>
                                                        <Button className="w-full justify-start">Get Started</Button>
                                                    </Link>
                                                </>
                                            )}
                                        </nav>
                                    </div>
                                </SheetContent>
                            </Sheet>
                        </div>
                    </div>
                </div>
            </header>
        </>
    )
}
